import { z } from 'zod';
import {
  EMAIL_REGEX,
  PEC_REGEX,
  VAT_NUMBER_REGEX,
  PHONE_REGEX,
  validateMultipleEmails,
  validateMultiplePhones,
} from './validationPatterns';

const isEmpty = (value?: string | null) =>
  value === undefined || value === null || value.trim() === '';

export const COMPANY_NAME_TYPE_OPTIONS = ['S.r.l.', 'S.p.A.', 'S.a.s.', 'S.c.', 'S.s.'];

export const customerFormSchema = z
  .object({
    company_name: z.string().min(1, "Il nome dell'azienda è obbligatorio"),
    company_name_type: z.string().optional(),
    ateco_2025: z.string().optional(),
    address: z.string().optional(),
    phone: z
      .string()
      .optional()
      .refine(value => isEmpty(value) || PHONE_REGEX.test(value as string), {
        message: 'Inserisci un numero di telefono valido',
      }),
    voluntary: z
      .boolean()
      .nullable()
      .refine(val => val !== null, {
        message: 'Campo obbligatorio',
      }),
    type: z.string().optional(),
    multi_site: z.boolean().nullable().optional(),
    clustering: z.string().optional(),
    employee_count: z.any().optional(),
    revenue: z.any().optional(),
    balance_sheet_assets: z.any().optional(),
    size_category: z.string().optional(),
    consumption_category: z.string().optional(),
    average_annual_consumption: z.any().optional(),
    high_electricity_consumption: z.boolean().nullable().optional(),
    high_gas_consumption: z.boolean().nullable().optional(),
    certifications: z.string().optional(),
    vat_number: z
      .string()
      .optional()
      .refine(value => isEmpty(value) || VAT_NUMBER_REGEX.test(value as string), {
        message: 'La partita IVA deve contenere 11 cifre',
      }),
    tax_code: z.string().optional(),
    certified_email: z
      .string()
      .optional()
      .refine(value => isEmpty(value) || validateMultipleEmails(value, PEC_REGEX), {
        message: 'Inserisci una o più PEC valide, separate da virgola',
      }),
    legal_representative: z.string().optional(),
    legal_representative_phone: z
      .string()
      .optional()
      .refine(value => isEmpty(value) || PHONE_REGEX.test(value as string), {
        message: 'Inserisci un numero di telefono valido',
      }),
    legal_representative_email: z
      .string()
      .optional()
      .refine(value => isEmpty(value) || validateMultipleEmails(value, EMAIL_REGEX), {
        message: 'Inserisci uno o più indirizzi email validi, separati da virgola',
      }),
    contact_person: z.string().optional(),
    contact_person_phone: z
      .string()
      .optional()
      .refine(value => isEmpty(value) || validateMultiplePhones(value, PHONE_REGEX), {
        message: 'Inserisci uno o più numeri di telefono validi, separati da virgola',
      }),
    contact_person_email: z
      .string()
      .optional()
      .refine(value => isEmpty(value) || validateMultipleEmails(value, EMAIL_REGEX), {
        message: 'Inserisci uno o più indirizzi email validi, separati da virgola',
      }),
  })
  .superRefine((data, ctx) => {
    if (data.voluntary === false) {
      const requiredFields = [
        { field: 'address', message: "L'indirizzo della sede legale è obbligatorio" },
        { field: 'type', message: 'La tipologia di impresa è obbligatoria' },
        { field: 'clustering', message: 'La clusterizzazione è obbligatoria' },
        { field: 'size_category', message: 'La categoria dimensionale è obbligatoria' },
        { field: 'vat_number', message: 'La partita IVA è obbligatoria' },
        { field: 'tax_code', message: 'Il codice fiscale è obbligatorio' },
        { field: 'certified_email', message: 'La PEC aziendale è obbligatoria' },
        { field: 'legal_representative', message: 'Il legale rappresentante è obbligatorio' },
        {
          field: 'contact_person',
          message: 'Il referente aziendale per la diagnosi è obbligatorio',
        },
      ];

      requiredFields.forEach(({ field, message }) => {
        const value = data[field as keyof typeof data];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: [field],
          });
        }
      });

      if (data.multi_site === undefined || data.multi_site === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Il campo Impresa monosito / multisito è obbligatorio',
          path: ['multi_site'],
        });
      }
      if (
        data.high_electricity_consumption === undefined ||
        data.high_electricity_consumption === null
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Il campo Impresa a forte consumo di energia elettrica è obbligatorio',
          path: ['high_electricity_consumption'],
        });
      }
      if (data.high_gas_consumption === undefined || data.high_gas_consumption === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Il campo Impresa a forte consumo di gas è obbligatorio',
          path: ['high_gas_consumption'],
        });
      }
    }
  });

export type CustomerFormData = z.infer<typeof customerFormSchema>;
